// src/components/PrintModal.tsx
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

interface PrintModalProps {
    showPrintModal: boolean;
    setShowPrintModal: (val: boolean) => void;
    jobsToPrint: any[];
    customers: any[]; 
    handlePrint: () => void;
    getCustomerName: (id: string) => string;
    userName?: string; 
}

export default function PrintModal({
    showPrintModal, setShowPrintModal, jobsToPrint,
    customers, handlePrint, getCustomerName, userName
}: PrintModalProps) {
    const [mounted, setMounted] = useState(false);
    const [isSafari, setIsSafari] = useState(false);
    const printContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        setIsSafari(isSafariBrowser);
    }, []);

    if (!showPrintModal || !mounted) return null;

    const formatPrice = (price: number | string | undefined) => {
        if (price === undefined || price === null) return '€ -,--';
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        return isNaN(numPrice) ? '€ -,--' : `€ ${numPrice.toFixed(2).replace('.', ',')}`;
    };

    const formatDeadline = (job: any) => {
        if (!job?.deadline) return 'In overleg';
        
        const date = new Date(job.deadline);
        const dateString = date.toLocaleDateString('nl-NL', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
        });

        if (job.deadlineTime) {
            return `${dateString} - ${job.deadlineTime}`;
        }
        
        return dateString;
    };

    // SAFARI FIX: Strikte millimeter-beperking zonder aspect-ratio overloop
    const printViaIframe = () => {
        if (!printContainerRef.current) return;
        
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const content = printContainerRef.current.innerHTML;
        const doc = iframe.contentWindow?.document;

        if (doc) {
            const styleTags = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
                .map(node => node.outerHTML)
                .join('\n');

            doc.open();
            doc.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Print Racketkaart</title>
                    ${styleTags}
                    <style>
                        @page { margin: 0; size: 105mm 148mm; }
                        * { box-sizing: border-box !important; }
                        html, body { 
                            margin: 0 !important; 
                            padding: 0 !important; 
                            width: 105mm !important; 
                            height: 148mm !important; 
                            background: white !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                            overflow: hidden !important;
                        }
                        .print-page-wrapper {
                            width: 105mm !important;
                            height: 148mm !important;
                            padding: 5mm 4mm 4mm 4mm !important; /* Dwingt 5mm ruimte af aan de bovenkant */
                            margin: 0 !important;
                            page-break-after: always;
                            break-inside: avoid;
                            display: block !important;
                        }
                        .print-page-wrapper:last-child { page-break-after: auto; }
                        
                        /* SAFARI KERN-FIX: Sloop de aspect-ratio en dwing 138mm hoogte af */
                        .print-card-container {
                            width: 100% !important;
                            height: 138mm !important; 
                            max-width: none !important;
                            max-height: none !important;
                            aspect-ratio: auto !important; 
                            border: 3px solid black !important;
                            display: flex !important;
                            flex-direction: column !important;
                            background: white !important;
                            box-sizing: border-box !important;
                        }
                    </style>
                </head>
                <body class="bg-white text-black">
                    ${content}
                </body>
                </html>
            `);
            doc.close();

            setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                setTimeout(() => {
                    if (document.body.contains(iframe)) {
                        document.body.removeChild(iframe);
                    }
                }, 1000);
            }, 150);
        }
    };

    const handleSafePrint = () => {
        if (isSafari) {
            printViaIframe();
        } else {
            setTimeout(() => {
                window.print();
            }, 100);
        }
    };

    // CSS VOOR CHROME & FIREFOX (Blijft exact zoals gewenst)
    const chromeFirefoxCSS = `
        @media print {
            @page { margin: 0; size: 105mm 148mm; }
            body > *:not(#print-modal-root) { display: none !important; }
            
            html, body { 
                width: 100% !important; 
                height: 100% !important; 
                margin: 0 !important; 
                padding: 0 !important; 
                background-color: white !important; 
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }

            #print-modal-root { 
                display: flex !important; 
                justify-content: center !important;
                align-items: center !important;
                position: absolute !important; 
                inset: 0 !important; 
                z-index: 999999 !important;
            }

            .print-clean-bg {
                background: white !important;
                background-color: white !important;
                box-shadow: none !important;
                border: none !important;
            }

            .print-page-wrapper { 
                position: relative !important; 
                display: flex !important; 
                justify-content: center !important;
                align-items: center !important;
                width: 100vw !important; 
                height: 100vh !important; 
                page-break-after: always !important; 
                break-inside: avoid !important; 
                margin: 0 !important; 
                padding: 2mm !important; 
                box-sizing: border-box !important; 
                overflow: hidden !important; 
            }

            .print-page-wrapper:last-child { page-break-after: auto !important; break-after: auto !important; }
            
            .print-card-container { 
                width: 100% !important; 
                height: 100% !important; 
                max-width: none !important; 
                max-height: none !important;
                margin: 0 !important; 
                border: 4px solid black !important; 
                box-sizing: border-box !important; 
                display: flex !important; 
                flex-direction: column !important; 
                text-align: left !important; 
                background-color: white !important; 
            }
            .print-hide { display: none !important; }
        }
    `;

    const modalContent = (
        <div id="print-modal-root" className="print-clean-bg fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 print:p-0 print:block print:static">
            
            {!isSafari && (
                <style type="text/css">
                    {chromeFirefoxCSS}
                </style>
            )}

            <div className="print-clean-bg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh] print:w-full print:max-w-none print:h-auto print:max-h-none print:overflow-visible print:rounded-none">
                
                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50 flex-shrink-0 print-hide">
                    <h3 className="font-bold text-lg text-slate-800 dark:text-white">Print Racketkaart (A6)</h3>
                    <button onClick={() => setShowPrintModal(false)}>
                        <X className="w-5 h-5 text-slate-400 hover:text-slate-600 transition-colors" />
                    </button>
                </div>

                <div className="print-clean-bg p-6 overflow-y-auto flex-grow print:p-0 print:overflow-visible print:block bg-slate-100 dark:bg-slate-800/50">
                    
                    {jobsToPrint.length > 1 && (
                        <p className="mb-4 text-center font-bold text-indigo-600 print-hide">
                            {jobsToPrint.length} racketkaarten geselecteerd
                        </p>
                    )}
                    
                    <div ref={printContainerRef} className="print-clean-bg flex flex-col items-center print:block">
                        
                        {jobsToPrint.map((job, index) => {
                            const customer = customers?.find(c => c.id === job?.customerId);
                            const customerName = job?.customerId ? getCustomerName(job.customerId) : 'Naam Speler';
                            const dateStr = job?.completedAt ? new Date(job.completedAt).toLocaleDateString('nl-NL') : new Date().toLocaleDateString('nl-NL');
                            
                            const deadlineStr = formatDeadline(job);

                            let stringText = job?.string || 'Geen snaar opgegeven';
                            if (job?.isHybrid && job?.crossString) {
                                stringText = `M: ${job.string} / C: ${job.crossString}`;
                            }
                            const hasGrip = job?.baseGrip || job?.overGrip;

                            return (
                                <div key={job.id || index} className={`print-clean-bg w-full ${index > 0 ? 'mt-6 print:block' : 'print:block'} flex justify-center print-page-wrapper`}>
                                    
                                    <div className="bg-white text-black box-border relative border-[4px] border-black print-card-container aspect-[1/1.41] print:aspect-auto w-full max-w-[380px] shadow-lg print:shadow-none flex flex-col mx-auto">
                                        
                                        <div 
                                            className="text-center py-1.5 mb-1.5 flex-shrink-0" 
                                            style={{ backgroundColor: '#000', color: '#fff', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
                                        >
                                            <h2 className="text-xl font-black uppercase tracking-widest m-0 leading-none">Bespanservice</h2>
                                        </div>

                                        <div className="flex flex-col flex-grow px-3 pb-1 justify-between">
                                            
                                            <div className="mb-1">
                                                <span className="block text-[10px] uppercase font-black text-black tracking-wider border-b-2 border-black mb-1">Klant & Racket</span>
                                                <span className="block text-2xl font-black leading-tight break-words">
                                                    {customerName}
                                                </span>
                                                {job?.racket && (
                                                    <span className="block text-lg font-bold mt-0.5 leading-tight">
                                                        Racket: {job.racket}
                                                    </span>
                                                )}
                                                {customer?.phone && (
                                                    <span className="block text-sm font-bold mt-0.5">{customer.phone}</span>
                                                )}
                                            </div>

                                            <div className="mb-1">
                                                <span className="block text-[10px] uppercase font-black text-black tracking-wider border-b-2 border-black mb-1">Specificaties</span>
                                                <span className="block text-2xl font-black leading-tight mb-1">
                                                    {stringText}
                                                </span>
                                                
                                                <div className="flex flex-nowrap justify-between items-center w-full">
                                                    <span className="text-xl font-black whitespace-nowrap">
                                                        Spankracht: {job?.tensionMain}/{job?.tensionCross}
                                                    </span>
                                                    <span className="text-xl font-black text-right whitespace-nowrap ml-2">
                                                        {job?.tensionUnit || 'kg'}
                                                    </span>
                                                </div>

                                                {hasGrip && (
                                                    <div className="mt-1 pt-1 border-t border-black border-dotted">
                                                        <span className="text-xs font-bold block mb-0.5">Inclusief:</span>
                                                        {job.baseGrip && <span className="text-xs font-bold block">- {job.baseGrip}</span>}
                                                        {job.overGrip && <span className="text-xs font-bold block">- {job.overGrip}</span>}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-1 w-full">
                                                <div className="flex flex-nowrap justify-between border-b-2 border-black mb-0.5 w-full">
                                                    <span className="block text-[10px] uppercase font-black text-black tracking-wider whitespace-nowrap">Inname</span>
                                                    <span className="block text-[10px] uppercase font-black text-black tracking-wider text-right whitespace-nowrap ml-2">Ophalen</span>
                                                </div>
                                                <div className="flex flex-nowrap justify-between items-center w-full">
                                                    <span className="block text-lg font-black whitespace-nowrap">{dateStr}</span>
                                                    <span className="block text-xl font-black text-right whitespace-nowrap ml-2">{deadlineStr}</span>
                                                </div>
                                            </div>

                                            <div className="mt-auto w-full">
                                                <span className="block text-[9px] uppercase font-black text-black tracking-wider border-b-2 border-black mb-1">Financieel</span>
                                                <div className="flex flex-nowrap justify-between items-end w-full">
                                                    <div className="flex-1 min-w-0 pr-2">
                                                        <span className="text-sm font-bold block mb-0.5 whitespace-nowrap">Te betalen bedrag</span>
                                                        <span className="text-3xl font-black leading-none whitespace-nowrap">{formatPrice(job?.price)}</span>
                                                    </div>
                                                    
                                                    {job?.paymentLink && (
                                                        <div className="flex flex-col items-center flex-shrink-0 w-[80px]">
                                                            <span className="text-[8px] font-bold uppercase mb-0.5 whitespace-nowrap">Scan & Betaal</span>
                                                            <div className="border-[2px] border-black p-1 bg-white w-[80px] h-[80px] flex justify-center items-center">
                                                                <QRCodeSVG 
                                                                    value={job.paymentLink} 
                                                                    style={{ width: '100%', height: '100%' }}
                                                                    level="L" 
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t-[4px] border-black py-1.5 px-2 text-center mt-auto flex-shrink-0" style={{ backgroundColor: '#fff', color: '#000' }}>
                                            <span className="text-xl font-black uppercase tracking-widest block w-full truncate">
                                                {userName || 'Uw Bespanner'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex-shrink-0 print-hide">
                    <button onClick={handleSafePrint} className="w-full bg-indigo-600 hover:bg-indigo-700 transition-colors text-white py-3 rounded-xl font-bold flex items-center justify-center">
                        <Printer className="w-5 h-5 mr-2" />
                        Print Kaart{jobsToPrint.length > 1 ? 'en' : ''}
                    </button>
                </div>

            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}